import { useSyncExternalStore } from 'react';

// ============================================================================
// insets.ts (E1/Paket L) — die EINE Inset-Quelle der App.
// ----------------------------------------------------------------------------
// Absorbiert den bisherigen components/chat/useKeyboardInset.ts: schreibt den
// Tastatur-Inset, das data-keyboard-open-Attribut und die effektive Hoehe als
// globalen CSS-Vertrag auf <html>. useKeyboardInset.ts bleibt als duenner
// Ref-Count-Wrapper bestehen (Composer ruft ihn weiter auf), damit die
// bestehenden Konsumenten (Composer.module.css, Tindra.module.css,
// hooks/useHardwareKeyboard.softKeyboardOpen) unveraendert weiterlaufen.
//
// Geschriebene Variablen/Attribute:
//   --keyboard-inset          px, Ueberlappung der Tastatur (BESTEHENDER Name,
//                             load-bearing fuer Composer/Tindra/useHardwareKeyboard)
//   --kb-inset                Alias von --keyboard-inset (docs/30 §5/§7.5 nutzt
//                             ihn in layout.css: --inset-bottom = max(safe, kb))
//   --vh-effective            px, effektive (sichtbare) Viewport-Hoehe
//   --window-controls-inset-* px, iPadOS-26-Fenstercontrols (heute 0, Web hat
//                             noch keine API; in E2 verdrahtet)
//   [data-keyboard-open]      gesetzt, sobald der Inset > 80px
//
// Berechnung (Pflichtform, docs/30 §7.5): NICHT innerHeight - vv.height, weil
// interactive-widget=resizes-content auf Android den Layout-Viewport bereits
// verkleinert und der Abstand sonst doppelt zaehlt.
// ============================================================================

const KEYBOARD_OPEN_THRESHOLD_PX = 80;

export interface InsetState {
  keyboard: number;
  keyboardOpen: boolean;
  effectiveHeight: number;
  windowControlsTop: number;
  windowControlsLeft: number;
}

const SERVER_STATE: InsetState = {
  keyboard: 0,
  keyboardOpen: false,
  effectiveHeight: 0,
  windowControlsTop: 0,
  windowControlsLeft: 0,
};

let state: InsetState = { ...SERVER_STATE };
const subscribers = new Set<() => void>();

function notify(): void {
  for (const cb of subscribers) cb();
}

function measure(): void {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  const vv = window.visualViewport;
  const layoutHeight = window.innerHeight;

  let keyboard = 0;
  let effectiveHeight = layoutHeight;
  if (vv && typeof vv.height === 'number') {
    keyboard = Math.max(0, layoutHeight - vv.height - (vv.offsetTop || 0));
    effectiveHeight = vv.height;
  }

  const keyboardOpen = keyboard > KEYBOARD_OPEN_THRESHOLD_PX;

  root.style.setProperty('--keyboard-inset', `${keyboard}px`);
  root.style.setProperty('--kb-inset', `${keyboard}px`);
  root.style.setProperty('--vh-effective', `${effectiveHeight}px`);

  if (keyboardOpen !== state.keyboardOpen) {
    if (keyboardOpen) {
      root.setAttribute('data-keyboard-open', 'true');
      // iOS-WKWebView: sobald die Tastatur erscheint, kann der fixed Header nach
      // oben aus dem Viewport rutschen. Einmaliges Reset auf (0,0) haelt ihn
      // sichtbar (uebernommen aus useKeyboardInset.ts, Feedbackrunde 4/6b).
      window.scrollTo(0, 0);
    } else {
      root.removeAttribute('data-keyboard-open');
    }
  }

  const changed =
    keyboard !== state.keyboard ||
    keyboardOpen !== state.keyboardOpen ||
    effectiveHeight !== state.effectiveHeight;
  state = { keyboard, keyboardOpen, effectiveHeight, windowControlsTop: 0, windowControlsLeft: 0 };
  if (changed) notify();
}

let refCount = 0;
let teardown: (() => void) | null = null;

function start(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const vv = window.visualViewport;
  measure();
  const onChange = () => measure();
  vv?.addEventListener('resize', onChange);
  vv?.addEventListener('scroll', onChange);
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  return () => {
    vv?.removeEventListener('resize', onChange);
    vv?.removeEventListener('scroll', onChange);
    window.removeEventListener('resize', onChange);
    window.removeEventListener('orientationchange', onChange);
    const root = document.documentElement;
    root.style.removeProperty('--keyboard-inset');
    root.style.removeProperty('--kb-inset');
    root.style.removeProperty('--vh-effective');
    root.removeAttribute('data-keyboard-open');
    state = { ...SERVER_STATE };
  };
}

/**
 * Startet die Inset-Bruecke (idempotent, ref-gezaehlt). Einmal aus App.tsx
 * aufrufen — dann steht der Vertrag app-weit. Zusaetzliche Aufrufer
 * (useKeyboardInset im Composer) erhoehen nur den Ref-Count; die Listener
 * bleiben installiert, solange irgendein Aufrufer aktiv ist. So verschwindet
 * --keyboard-inset NICHT, wenn ein einzelner Composer unmountet.
 */
export function installInsetBridge(): () => void {
  refCount += 1;
  if (refCount === 1) teardown = start();
  return () => {
    refCount -= 1;
    if (refCount <= 0) {
      refCount = 0;
      teardown?.();
      teardown = null;
    }
  };
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function getSnapshot(): InsetState {
  return state;
}

function getServerSnapshot(): InsetState {
  return SERVER_STATE;
}

/** Nur fuer JS-Entscheidungen (z.B. Autoscroll bei Tastatur). CSS liest die Variablen direkt. */
export function useInsets(): InsetState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
